/**
 * Prepare To Signing Page - PDF.js Implementation
 *
 * Replaces Apryse WebViewer with PDF.js while maintaining exact same interface
 * for SignersSidebar integration.
 */

import React, { useCallback, useState, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useDocumentState } from '../../contexts/DocumentContext';
import { DocumentBadgeContainer } from '../../components/document-wizard/document-badge-container/DocumentBadgeContainer';
import { DocumentWizardHeaderLastSaved } from '../../components/document-wizard/document-wizard-header/document-wizard-header-lastsaved/DocumentWizardHeaderLastSaved';
import { DocumentWizardHeaderContainer } from '../../components/document-wizard/document-wizard-header/DocumentWizardHeaderContainer';
import { DocumentPermissions } from '../document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { SignersSidebar } from '../../components/document-wizard/document-editor/components/signersSidebar';
import LeaseWizardSideNav from '../../components/document-wizard/document-wizard-sidenav/documentWizardSideNav';
import { AnnotationTypes } from '../../components/document-wizard/document-editor/components/signersSidebar/components/insertFields/insertFields';
import { ImportedDocumentSupportedContentTypes } from '../../components/shared/modals/fileUploadModal/types';
import { DocumentStatusEnum } from '../../shared/constants/document';

// PDF.js components
import { PDFJSViewer } from '../../components/pdf-viewer/PDFJSViewer';
import { useFieldPlacements, FieldPlacement } from '../../components/pdf-viewer/hooks/useFieldPlacements';
import {
  convertScreenToPdfCoords,
  getPageNumberFromScreenCoords,
  getDefaultFieldDimensions,
  getSignerColor,
} from '../../components/pdf-viewer/utils/coordinates';
import { PDFPageInfo } from '../../components/pdf-viewer/types';
import { getDocumentById } from '../../api/queries_v2';

import styles from './styles.module.scss';
import './styles.module.scss';

export interface PrepareToSigningPageProps {
  lastUpdated: string;
  documentTitle: string;
  isDocumentCreator: boolean;
  documentId: string;
  permissions: DocumentPermissions;
  handleClose: () => void;
  pdfUrl: string;
  isDocumentLocked: boolean;
  dealId: string;
  teamId: string;
  documentContentType: ImportedDocumentSupportedContentTypes;
  documentStatus: string;
  documentSource?: string;
}

export type FieldToAdd = {
  type: AnnotationTypes;
  name: string;
  value: string;
  signerId: string;
  signerOrder: number;
  dropPoint: { x: number; y: number };
};

const PrepareToSigningPdfJs = ({
  lastUpdated,
  documentTitle,
  documentId,
  permissions,
  handleClose,
  pdfUrl,
  isDocumentLocked,
  isDocumentCreator,
  dealId,
  teamId,
  documentStatus,
  documentSource,
}: PrepareToSigningPageProps) => {
  const viewer = useRef<HTMLDivElement>(null);
  const [pagesInfo, setPagesInfo] = useState<PDFPageInfo[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const { updateDocumentState } = useDocumentState();

  const isSigningProcessStarted = [
    DocumentStatusEnum.PREPARING_TO_SIGN,
    DocumentStatusEnum.SIGNING,
    DocumentStatusEnum.EXECUTED,
  ].includes(documentStatus as DocumentStatusEnum);
  const isDocumentExecuted = documentStatus === DocumentStatusEnum.EXECUTED;

  // Load existing field placements from GraphQL
  const { data: documentData, refetch: refetchDocument } = useQuery(getDocumentById, {
    variables: { documentId },
    skip: !documentId,
  });

  // Memoize initialFields to prevent unnecessary re-renders
  const initialFields: FieldPlacement[] = React.useMemo(() => {
    const fields = (documentData?.getDocumentById?.fieldPlacements || []) as FieldPlacement[];
    return fields;
  }, [documentData?.getDocumentById?.fieldPlacements, documentId, documentData]);

  // Get form fields and annotations from document for readonly display
  const savedPdfFormFields = React.useMemo(() => {
    return documentData?.getDocumentById?.pdfFormFields || [];
  }, [documentData?.getDocumentById?.pdfFormFields]);

  const savedPdfAnnotations = React.useMemo(() => {
    return documentData?.getDocumentById?.pdfAnnotations || [];
  }, [documentData?.getDocumentById?.pdfAnnotations]);

  // Field placements management with auto-save
  const {
    fields,
    addField: addFieldPlacement,
    updateField: updateFieldPlacement,
    deleteField: deleteFieldPlacement,
    saving,
  } = useFieldPlacements({
    documentId,
    initialFields,
    onSaveSuccess: async () => {
      // Refetch document to get updated timestamp
      const { data } = await refetchDocument();
      // Update document state with new timestamp for "Last saved" indicator
      if (data?.getDocumentById?.updatedAt) {
        updateDocumentState({ lastUpdated: data.getDocumentById.updatedAt });
      }
    },
    onSaveError: (error) => {
      console.error('Field placement save failed:', error);
    },
  });

  /**
   * Add field at drop point (called by SignersSidebar)
   * This maintains the exact same interface as Apryse version
   */
  const addField = useCallback(({ type, value = '', signerId, signerOrder, dropPoint }: FieldToAdd) => {
    if (pagesInfo.length === 0) {
      console.warn('Pages not loaded yet');
      return;
    }

    // Determine which page the drop occurred on
    const pageNumber = getPageNumberFromScreenCoords(dropPoint.x, dropPoint.y, pagesInfo);
    if (!pageNumber) {
      console.warn('Drop point not on any page');
      return;
    }

    // Convert screen coordinates to PDF coordinates
    const pdfCoords = convertScreenToPdfCoords(dropPoint.x, dropPoint.y, pageNumber, pagesInfo);
    if (!pdfCoords) {
      console.warn('Failed to convert coordinates');
      return;
    }

    // Get default dimensions for this field type
    const dimensions = getDefaultFieldDimensions(
      type as 'signature' | 'initials' | 'date' | 'text'
    );

    // Format field type for label (signature → Signature, date → Date)
    // Note: AnnotationTypes.DATE is 'date_signed' but gets normalized to 'date' for storage
    const typeLabel = type === AnnotationTypes.DATE
      ? 'Date'
      : type.charAt(0).toUpperCase() + type.slice(1);

    // Center field horizontally on cursor position (cursor at top-center of field)
    const centeredX = pdfCoords.x - dimensions.width / 2;

    // Add field via useFieldPlacements hook (auto-saves after 500ms)
    addFieldPlacement({
      type: type as 'signature' | 'initials' | 'date' | 'text',
      page: pageNumber,
      x: centeredX,
      y: pdfCoords.y,
      w: dimensions.width,
      h: dimensions.height,
      signer: signerId,
      req: true,
      data: {
        label: typeLabel, // Just "Signature", "Initials", etc.
        color: getSignerColor(signerOrder), // Order-based color (consistent)
        signerEmail: value, // For tooltip
        signerOrder, // Store order for persistence
      },
    });
  }, [pagesInfo, addFieldPlacement]);

  /**
   * Update signer email for all fields belonging to a signer
   */
  const handleUpdateAnnotations = useCallback(async (signerId: string, email: string) => {
    // Find all fields for this signer
    const signerFields = fields.filter(f => f.signer === signerId);

    // Update each field's email in data (label stays as field type only)
    signerFields.forEach(field => {
      updateFieldPlacement(field.id, {
        data: {
          ...field.data,
          signerEmail: email, // Update email for tooltip
          // Label remains as field type (e.g., "Signature", "Initials")
        },
      });
    });
  }, [fields, updateFieldPlacement]);

  /**
   * Delete all fields belonging to a signer
   */
  const handleDeleteAnnotations = useCallback((signerId: string) => {
    // Find all fields for this signer
    const signerFields = fields.filter(f => f.signer === signerId);

    // Delete each field
    signerFields.forEach(field => {
      deleteFieldPlacement(field.id);
    });
  }, [fields, deleteFieldPlacement]);

  /**
   * Handle PDF.js viewer ready - capture pages info for coordinate conversion
   */
  const handleViewerReady = useCallback((pages: PDFPageInfo[]) => {
    setPagesInfo(pages);
  }, []);

  const dragOver = (e: any) => {
    e.preventDefault();
    return false;
  };

  const drop = (e: any) => {
    e.preventDefault();
    return false;
  };

  // Derive permission flags from permissions object - matches DocumentPage.tsx logic
  // In PREPARING_TO_SIGN status, user should be able to send for signing and manage internal approval
  const canShare = permissions?.hasInviteSendingPermission ?? false;
  const canRequestInternalApproval = permissions?.hasEditPermission ?? false;

  return (
    <div className={styles.wrapper}>
      <DocumentWizardHeaderContainer
        canEdit={permissions?.hasEditPermission || false}
        onClose={handleClose}
        title={documentTitle}
        titleBadge={<DocumentBadgeContainer />}
        lastSaved={<DocumentWizardHeaderLastSaved datetime={lastUpdated} intervalInSecond={60} />}
        canShare={canShare}
        canRequestInternalApproval={canRequestInternalApproval}
        documentId={documentId}
        dealId={dealId}
        teamId={teamId}
        permissions={permissions}
        isDocumentLocked={isDocumentLocked}
        documentSource={documentSource as any}
      />
      <div className="d-flex overflow-hidden w-100" style={{ flex: 1 }}>
        <LeaseWizardSideNav disableClose isCounterparty={!isDocumentCreator} onClose={handleClose}>
          <aside>
            <SignersSidebar
              permissions={permissions}
              addField={addField}
              updateAnnotations={handleUpdateAnnotations}
              deleteAnnotations={handleDeleteAnnotations}
              shouldShowInsertionUi
              isDocumentLocked={isDocumentLocked}
              viewer={viewer.current}
            />
          </aside>
        </LeaseWizardSideNav>

        {/* PDF.js Viewer Container */}
        <div
          className={styles.pdfViewerContainer}
          ref={viewer}
          onDragOver={dragOver}
          onDrop={drop}
        >
          <PDFJSViewer
            pdfUrl={pdfUrl}
            mode="read-only"
            showControls={!isDocumentExecuted}
            showThumbnails={!isDocumentExecuted}
            onPagesReady={handleViewerReady}
            savedPdfFormFields={savedPdfFormFields}
            savedPdfAnnotations={savedPdfAnnotations}
            hideComments={true}
            fieldOverlayProps={!isDocumentExecuted ? {
              fields,
              selectedFieldId,
              onSelectField: setSelectedFieldId,
              onUpdateField: updateFieldPlacement,
              onDeleteField: deleteFieldPlacement,
            } : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default PrepareToSigningPdfJs;
